// ════════════════════════════════════════════════════════════════════
// ILLEGAL EMPIRE — ACHIEVEMENT SYSTEM  (THE CONNECTIVE TISSUE)
// ────────────────────────────────────────────────────────────────────
// Canonical implementation. Terror owns this file. Pierre, Snake Eyes,
// Sol Survivor, and every future IE game adopt it by copy-paste.
//
// This is the registry + reward-gating layer. It is to achievements what
// companions.js is to companions and the cosmetic catalog is to cosmetics:
// the SINGLE SOURCE OF TRUTH. IDs are app-agnostic strings. Every game
// maps an ID to its own presentation, but the canonical definition lives
// here and is identical across all IE apps.
//
// ── WHY THIS IS THE WOW FACTOR ───────────────────────────────────────
// Achievements are the glue that turns "separate games on one login" into
// ONE interconnected universe. Because every achievement is written to the
// shared profile (profiles/{uid}/achievements/{id}) — and because that node
// is Cloudflare-verifiable — any IE game can read what a player has earned
// ANYWHERE and gate its own rewards on it.
//
//   "Beat J'aeonxi in Terror"  → unlocks a radiant skin offer in Snake Eyes.
//   "Win 50 rounds at the Pier" → unlocks a Terror cosmetic.
//   "Earn all four affinities" → unlocks a cross-game title.
//
// The achievement is the key. The reward gate (below) is the lock.
//
// ── THE THREE LAYERS ─────────────────────────────────────────────────
//   1. ACHIEVEMENTS    — what can be earned (this registry)
//   2. grantAchievement — the write path (progression.js, append-only)
//   3. REWARD_GATES     — what an achievement unlocks elsewhere (this file)
//
// ── RULES ────────────────────────────────────────────────────────────
//   • IDs are namespaced by game of origin: "terror:...", "pier:...",
//     "casino:...", "sol:...", "empire:..." (empire = cross-game meta).
//   • IDs are PERMANENT. Once shipped, never rename — provenance breaks.
//   • Append-only. Earning is forever. No achievement is ever revoked.
//   • Shape MUST stay identical across all IE copies. If you change this
//     file, bump SCHEMA_VERSION, then propagate to all apps the same day.
// ════════════════════════════════════════════════════════════════════

export const SCHEMA_VERSION = "1.6"; // 2026-10-21 — the four kit copies (Terror, the Pier,
                                      // Shatterproof, Annihilation) re-joined as ONE file:
                                      // the 1.5 registry + Terror's Ilk wording + the heart
                                      // icons. Shape unchanged.
                                      // 1.5 was: Registration sweep:
                                      // every real, substantial-codebase title in
                                      // the Empire now has a GAME_NAMESPACES entry
                                      // (dtd + 14 new titles: rush, bodies, hoc,
                                      // hooked, impera, jynxibash, kindred, tilt,
                                      // 88, duhm, echoskew, vygil, miniwili,
                                      // fractured). Matching GAME_PORTALS doors
                                      // added in UP's UnstablePierre.js for the
                                      // same set plus perry/swat/bebecitos/
                                      // annihilation/lyraea, which already had
                                      // namespaces but no door. ACTIVE_GAMES is
                                      // UNCHANGED — none of these are publicly
                                      // playable with live grants yet. hoc/hoc-3d
                                      // are ONE title (two render backends),
                                      // registered once as `hoc`.
                                      // 1.4 was: `swat` registered
                                      // (the last currency-owning title that
                                      // wasn't), Annihilation given TWO entries
                                      // of its own so it stops borrowing
                                      // Terror's identity, and the first THREE
                                      // CROSS-GAME GATES added as a derived
                                      // layer — see CROSS_GAME_GATES near the
                                      // bottom. No call site changed anywhere:
                                      // deriveMetaAchievements() already runs
                                      // after every grant in every title.
                                      // 1.3 was — THE TRUE
                                      // GREAT CALIBRATION. The registry had
                                      // drifted FIVE ways (Terror 1.2,
                                      // Annihilation 1.1, gruel/Perry/Pierre
                                      // 1.0) for three weeks. 1.3 is the
                                      // reunification: `perry` registered in
                                      // GAME_NAMESPACES, Perry's 22 entries
                                      // folded into the trunk, and the PVP
                                      // family restored to the three copies
                                      // that had lost it. The canonical copy
                                      // now lives at gamemaster/engine/ —
                                      // diff against THAT, not against a
                                      // sibling app.
                                      // 1.2 was: GAME_NAMESPACES gained
                                      // `icon`, making this the ONE source
                                      // for a game's label, tint AND glyph.
                                      // 1.1 was: bebecitos + annihilation
                                      // registered, sol pulled from
                                      // ACTIVE_GAMES (Citizen was unearnable).
                                      // Every IE copy of this file must match.

// ── GAME NAMESPACES ───────────────────────────────────────────────────
// Every achievement belongs to exactly one origin game (or "empire" for
// cross-game meta-achievements that no single game owns). The prefix on
// the ID and the `game` field must agree.
//
// REGISTRATION LAW (IE_FIREBASE_LAW Part 2, rule 9): a title is registered
// HERE the day it has a name. Registration is free — it grants a label and
// a tint, and carries ZERO grant semantics. It does NOT gate Citizen.
// Gating Citizen is ACTIVE_GAMES' job, at the bottom of this file, and a
// title only joins that list when it is publicly playable with live grants.
// Keeping these two separate is what lets a new title ship achievements
// without making Citizen retroactively harder for everyone mid-flight.
//
// THREE FIELDS, ONE SOURCE (added SCHEMA 1.2). A game's `label`, `tint` and
// `icon` all live here and nowhere else. Bebecitos previously hand-kept a
// parallel NAMESPACE_META with its own labels+icons; that is now derived
// from its synced copy of THIS object. The drift that fix prevents is
// specific and load-bearing: THE MARK §7 renders a being's migration chain
// by resolving namespace ids to labels, and THE_MARK.md hard rule 3 says a
// Mark must read identically in every game. Two label maps = the same being
// reading differently depending on which game you opened it in.
//
// `tint` is the game's chrome accent. `icon` is its glyph for compact
// surfaces (badge shelves, chain rows) where a color alone can't identify
// a game. Per-game RARITY tint tables are a SEPARATE thing and are allowed
// to differ freely — see the RARITY block below.
export const GAME_NAMESPACES = {
  terror:      { label: "Terror",           tint: "#c8963c", icon: "🃏" },
  pier:        { label: "W'ily's Pier",     tint: "#4da3a3", icon: "🎡" },
  casino:      { label: "Snake Eyes",       tint: "#c94a6b", icon: "🎲" },
  sol:         { label: "Sol Survivor",     tint: "#e8b84b", icon: "☀" },
  perry:       { label: "Perry",            tint: "#e0763a", icon: "🛡" },
  swat:        { label: "S'wat",           tint: "#c9b48b", icon: "⌁" },
  bebecitos:   { label: "Bebecitos",        tint: "#7fb98a", icon: "🌱" },
  annihilation:{ label: "Annihilation",     tint: "#a83b3b", icon: "⚔" },
  lyraea:      { label: "Lyraea",           tint: "#b98ac9", icon: "♪" },
  empire:      { label: "Illegal Empire",   tint: "#8a5cd6", icon: "◈" },
  // ── 1.5 additions (2026-09-01) ─ registration sweep. Every title with a
  // real, substantial codebase in the Empire now has a namespace, per
  // caP'n's instruction to make every game "a part of UP with the Mark
  // implemented, achievements, etc." Registration is free (see the law
  // above) — none of these join ACTIVE_GAMES, which stays untouched.
  // hoc covers BOTH the house-of-cards 2D and hoc-3d repos — one title,
  // two render backends, not two games.
  dtd:         { label: "Dusk till Dawn",   tint: "#f87171", icon: "🕯" },
  rush:        { label: "Ruin Rush",        tint: "#e2724f", icon: "🏎" },
  bodies:      { label: "Bodies",           tint: "#b33a3a", icon: "🥊" },
  hoc:         { label: "House of Cards",   tint: "#7a3b3b", icon: "🏚" },
  hooked:      { label: "Hooked",           tint: "#2a7f9e", icon: "🎣" },
  impera:      { label: "Era of Imps",      tint: "#6b4ba3", icon: "😈" },
  jynxibash:   { label: "Jynxi Bash",       tint: "#3d6fae", icon: "🚀" },
  kindred:     { label: "Kindred",          tint: "#c97fae", icon: "💞" },
  tilt:        { label: "Tilt",             tint: "#f0b429", icon: "🕹" },
  "88":        { label: "88 Ways to Die",   tint: "#5a5a6e", icon: "🎹" },
  duhm:        { label: "Duhm",             tint: "#5b8a72", icon: "📖" },
  echoskew:    { label: "Echo-Skew",        tint: "#3fb8ae", icon: "🌀" },
  vygil:       { label: "Vy'gil",           tint: "#e0c94b", icon: "🕯" },
  miniwili:    { label: "Mini-Wili's",      tint: "#4fa3c9", icon: "⛳" },
  fractured:   { label: "Fractured",        tint: "#6a5acd", icon: "🔺" },
  // 2026-10-24 — Moonshine Masters (the Mark + co-op). Registration only: it does NOT join ACTIVE_GAMES.
  moonshine:   { label: "Moonshine Masters", tint: "#e8a84a", icon: "🌙" },
};

// Glyph for a namespace, with a neutral fallback so an unregistered id
// renders as a mark rather than as `undefined`.
export const getGameIcon = (gameId) =>
  (GAME_NAMESPACES[gameId] && GAME_NAMESPACES[gameId].icon) || "◈";

// Label for a namespace. Falls back to the raw id rather than dropping it —
// a chain with an unknown game still shows a complete chain.
export const getGameLabel = (gameId) =>
  (GAME_NAMESPACES[gameId] && GAME_NAMESPACES[gameId].label) || gameId;

// ── RARITY ────────────────────────────────────────────────────────────
// Mirrors the cosmetic rarity ladder so the whole IE universe speaks one
// language. Each game tints these to its own aesthetic.
export const ACHIEVEMENT_RARITIES = [
  "common", "uncommon", "rare", "epic", "legendary", "mythic",
];

export const ACHIEVEMENT_RARITY_META = {
  common:    { order: 0, label: "Common",    short: "C", tint: "#9aa4ad" },
  uncommon:  { order: 1, label: "Uncommon",  short: "U", tint: "#4da36a" },
  rare:      { order: 2, label: "Rare",      short: "R", tint: "#4a88c8" },
  epic:      { order: 3, label: "Epic",      short: "E", tint: "#8a5cd6" },
  legendary: { order: 4, label: "Legendary", short: "L", tint: "#d4a84b" },
  mythic:    { order: 5, label: "Mythic",    short: "M", tint: "#c94a6b" },
};

// ── THE REGISTRY ──────────────────────────────────────────────────────
// Every achievement in the Illegal Empire. App-agnostic. The single
// source of truth. Each entry:
//
//   id      — permanent, namespaced. NEVER rename after shipping.
//   game    — origin namespace key (must match the id prefix).
//   name    — display title.
//   desc    — how it's earned (player-facing).
//   rarity  — one of ACHIEVEMENT_RARITIES.
//   icon    — emoji/glyph the games can show (each game may override).
//   hidden  — if true, name/desc are masked until earned (secret achievement).
//   points  — IE-wide "renown" weight; cosmetic, fuels future leaderboards.
//
// NOTE: This is the launch set. Adding an achievement = add an entry here
// and call grantAchievement(uid, id) at the moment it's earned. The toast,
// gallery, and any reward gate light up automatically — registry-driven.
export const ACHIEVEMENTS = {
  // ── TERROR ───────────────────────────────────────────────────────────
  "terror:first_blood": {
    id: "terror:first_blood", game: "terror",
    name: "First Blood",
    desc: "Win your very first match in Terror.",
    rarity: "common", icon: "🩸", points: 10,
  },
  "terror:first_pack": {
    id: "terror:first_pack", game: "terror",
    name: "Crack the Seal",
    desc: "Open your first booster pack.",
    rarity: "common", icon: "♥", points: 10,
  },
  "terror:collector_50": {
    id: "terror:collector_50", game: "terror",
    name: "Hoarder's Instinct",
    desc: "Collect 50 unique cards.",
    rarity: "uncommon", icon: "🗃", points: 25,
  },
  "terror:collector_all": {
    id: "terror:collector_all", game: "terror",
    name: "The Complete Codex",
    desc: "Collect all 181 unique cards.",
    rarity: "mythic", icon: "📖", points: 250,
  },
  "terror:first_holo": {
    id: "terror:first_holo", game: "terror",
    name: "Caught the Light",
    desc: "Pull your first holographic card.",
    rarity: "uncommon", icon: "◈", points: 20,
  },
  "terror:jynxi_holo": {
    id: "terror:jynxi_holo", game: "terror",
    name: "Touched by the Trickster",
    desc: "Obtain a J'aeonxi-green holographic card.",
    rarity: "legendary", icon: "♥", points: 100,
  },
  "terror:dungeon_clear": {
    id: "terror:dungeon_clear", game: "terror",
    name: "Threshold Crossed",
    desc: "Clear your first dungeon.",
    rarity: "rare", icon: "⚔", points: 50,
  },
  "terror:d1_all_secrets": {
    id: "terror:d1_all_secrets", game: "terror",
    name: "Nothing Left Buried",
    desc: "Uncover every secret hidden in the Outskirts of the Minerium.",
    rarity: "epic", icon: "✶", points: 80,
  },
  "terror:oath_tablet": {
    id: "terror:oath_tablet", game: "terror",
    name: "The Order Before the Order",
    desc: "Solve the Oath Tablet and claim what should not exist.",
    rarity: "epic", icon: "◈", points: 90,
    hidden: true,
  },
  "terror:beat_jynxi": {
    id: "terror:beat_jynxi", game: "terror",
    name: "The Nefarious Undone",
    desc: "Defeat J'aeonxi the Nefarious.",
    rarity: "legendary", icon: "👑", points: 150,
  },
  "terror:depths_solrend": {
    id: "terror:depths_solrend", game: "terror",
    name: "Solrend's Equal",
    desc: "Reach Solrend rank in Endless Depths.",
    rarity: "epic", icon: "🌊", points: 80,
  },
  "terror:streak_50": {
    id: "terror:streak_50", game: "terror",
    name: "The Unbroken",
    desc: "Win 50 matches in a row.",
    rarity: "mythic", icon: "♾", points: 200,
  },
  "terror:radiant_first": {
    id: "terror:radiant_first", game: "terror",
    name: "It Shines for You",
    desc: "Equip your first radiant companion sheen.",
    rarity: "rare", icon: "✧", points: 40,
  },
  "terror:pet_100": {
    id: "terror:pet_100", game: "terror",
    name: "Best Friends",
    desc: "Pet your companion 100 times.",
    rarity: "uncommon", icon: "🩷", points: 25,
  },
  "terror:first_forge": {
    id: "terror:first_forge", game: "terror",
    name: "Crafted Your First",
    desc: "Build and save your first deck in the Forge.",
    rarity: "uncommon", icon: "🔨", points: 20,
  },
  "terror:first_freeplay": {
    id: "terror:first_freeplay", game: "terror",
    name: "Off the Rails",
    desc: "Win your first match in Free Play.",
    rarity: "uncommon", icon: "🎪", points: 20,
  },
  "terror:cosmetic_first": {
    id: "terror:cosmetic_first", game: "terror",
    name: "Dress to Impress",
    desc: "Equip your first cosmetic.",
    rarity: "uncommon", icon: "👗", points: 20,
  },
  "terror:collector_100": {
    id: "terror:collector_100", game: "terror",
    name: "The Hoarder's Haul",
    desc: "Collect 100 unique cards.",
    rarity: "rare", icon: "💎", points: 60,
  },
  "terror:depths_200_wins": {
    id: "terror:depths_200_wins", game: "terror",
    name: "Seasoned Depths Traveler",
    desc: "Win 200 matches in Endless Depths.",
    rarity: "rare", icon: "⚔", points: 70,
  },
  "terror:depths_champion": {
    id: "terror:depths_champion", game: "terror",
    name: "Champion's Crown",
    desc: "Reach Champion rank in Endless Depths.",
    rarity: "epic", icon: "👑", points: 90,
  },
  "terror:all_starters": {
    id: "terror:all_starters", game: "terror",
    name: "Starter Master",
    // ⭐ ILK, SOLIDIFIED (caP'n, 2026-10-21: "for those 4, they are Ilk"). The TOP-RIGHT
    // word on a card is its Ilk (RULEZ_V2 ch.6): Astraea · Phantom · Time · Dragon.
    // "Space" was the old name; a 2026-09-14 note calling this the "School" was wrong
    // and is retired. Kin (top-left) is Humanoid · Beast · Machine · Dragon.
    desc: "Win a Free Play match with a starter deck of each Ilk — Astraea, Phantom, Time, and Dragon.",
    rarity: "rare", icon: "📚", points: 50,
  },
  "terror:holo_5": {
    id: "terror:holo_5", game: "terror",
    name: "Gleaming Collection",
    desc: "Collect 5 holographic cards.",
    rarity: "rare", icon: "♥", points: 40,
  },
  // ⚠ THE ID STAYS `five_affinity` DELIBERATELY. There are four
  // Affinities, not five, and the trigger now asks for all four — but
  // an achievement id is a KEY IN EVERY PLAYER'S SAVE, so renaming it
  // would orphan every grant already earned under the old id. The id is
  // a misnomer; the name, the description and the title it feeds are
  // now correct. Never rename an achievement id to fix a display bug.
  "terror:five_affinity": {
    id: "terror:five_affinity", game: "terror",
    name: "Affinity Complete",
    desc: "Own cards from all four Affinities.",
    rarity: "rare", icon: "🌈", points: 30,
  },
  "terror:story_complete": {
    id: "terror:story_complete", game: "terror",
    name: "The Story Unfolds",
    desc: "Complete all story dungeons and defeat J'aeonxi.",
    rarity: "legendary", icon: "📖", points: 180,
  },
  "terror:depths_aenarchist": {
    id: "terror:depths_aenarchist", game: "terror",
    name: "Aenarchist's Path",
    desc: "Reach Aenarchist rank in Endless Depths.",
    rarity: "epic", icon: "🌊", points: 100,
  },
  "terror:gatekeeper_first": {
    id: "terror:gatekeeper_first", game: "terror",
    name: "The Guardian Falls",
    desc: "Defeat Rük the Gatekeeper for the first time.",
    rarity: "epic", icon: "⚱", points: 85,
  },
  "terror:freeplay_50": {
    id: "terror:freeplay_50", game: "terror",
    name: "Free Spirit",
    desc: "Win 50 matches in Free Play.",
    rarity: "rare", icon: "🎪", points: 60,
  },
  "terror:depths_500_wins": {
    id: "terror:depths_500_wins", game: "terror",
    name: "Depths Immortal",
    desc: "Win 500 matches in Endless Depths.",
    rarity: "legendary", icon: "♾", points: 140,
  },
  "terror:pet_500": {
    id: "terror:pet_500", game: "terror",
    name: "Companion's Beloved",
    desc: "Pet your companion 500 times.",
    rarity: "epic", icon: "💗", points: 70,
  },
  "terror:void_collector": {
    id: "terror:void_collector", game: "terror",
    name: "Void's Embrace",
    desc: "Own at least one card from the Void Eclipse pack.",
    rarity: "uncommon", icon: "🌑", points: 15,
  },
  "terror:three_radiant": {
    id: "terror:three_radiant", game: "terror",
    name: "Radiant Trinity",
    desc: "Equip radiant sheens on 3 different companions.",
    rarity: "epic", icon: "✧", points: 75,
  },
  // ── PVP family (C8, July 17 2026) — granted by recordPvpResult ──
  "terror:first_pvp": {
    id: "terror:first_pvp", game: "terror",
    name: "Warlock's Duel",
    desc: "Win your first Solrend against another player.",
    rarity: "uncommon", icon: "⚔", points: 30,
  },
  "terror:pvp_10": {
    id: "terror:pvp_10", game: "terror",
    name: "Duelist of Renown",
    desc: "Win 10 Solrends against other players.",
    rarity: "rare", icon: "🗡", points: 60,
  },

  // ── W'ILY'S PIER ──────────────────────────────────────────────────────
  "pier:first_exchange": {
    id: "pier:first_exchange", game: "pier",
    name: "Coin of the Realm",
    desc: "Make your first currency exchange at the Crest Well.",
    rarity: "common", icon: "🪙", points: 10,
  },
  "pier:wanderer": {
    id: "pier:wanderer", game: "pier",
    name: "Pier Wanderer",
    desc: "Visit every corner of W'ily's Pier.",
    rarity: "uncommon", icon: "🧭", points: 25,
  },

  // ── SNAKE EYES (CASINO) ───────────────────────────────────────────────
  "casino:first_roll": {
    id: "casino:first_roll", game: "casino",
    name: "Beginner's Luck",
    desc: "Win your first round at Snake Eyes.",
    rarity: "common", icon: "🎲", points: 10,
  },
  "casino:snake_eyes": {
    id: "casino:snake_eyes", game: "casino",
    name: "Snake Eyes",
    desc: "Roll the namesake — double ones.",
    rarity: "rare", icon: "🐍", points: 50,
  },
  "casino:high_roller": {
    id: "casino:high_roller", game: "casino",
    name: "High Roller",
    desc: "Win 1,000 Snake Eyes coins in a single run.",
    rarity: "epic", icon: "💰", points: 80,
  },

  // ── SOL SURVIVOR ──────────────────────────────────────────────────────
  "sol:first_dungeon": {
    id: "sol:first_dungeon", game: "sol",
    name: "Into the Sol",
    desc: "Complete your first Sol Survivor dungeon.",
    rarity: "uncommon", icon: "☀", points: 25,
  },
  "sol:full_party": {
    id: "sol:full_party", game: "sol",
    name: "T'aez, S'irinka & You",
    desc: "Recruit a full co-op party.",
    rarity: "rare", icon: "🤝", points: 50,
  },

  // ── PERRY ────────────────────────────────────────────────────────────
  // The parry pilgrimage. Namespace registered 2026-07-26 (Firebase Law
  // #9 — register on day one). Perry joins ACTIVE_GAMES on launch day,
  // not before. Propagate this file to every other IE app per the
  // adoption guide when Perry ships.
  "perry:first_parry": {
    id: "perry:first_parry", game: "perry",
    name: "The First Answer",
    desc: "Land your first parry.",
    rarity: "common", icon: "🛡", points: 10,
  },
  "perry:first_perfect": {
    id: "perry:first_perfect", game: "perry",
    name: "Perry's Own",
    desc: "Land your first PERFECT parry.",
    rarity: "common", icon: "♥", points: 10,
  },
  "perry:reach_hollowmere": {
    id: "perry:reach_hollowmere", game: "perry",
    name: "Out of the Ashes",
    desc: "Walk the Cinders Road and reach Hollowmere.",
    rarity: "uncommon", icon: "🏮", points: 25,
  },
  "perry:first_ascend": {
    id: "perry:first_ascend", game: "perry",
    name: "The Beb Rises",
    desc: "Spend your first skill point at the Ember Constellation.",
    rarity: "common", icon: "⭑", points: 15,
  },
  "perry:chain_10": {
    id: "perry:chain_10", game: "perry",
    name: "Ten in Tempo",
    desc: "Answer 10 strikes in a row without a miss.",
    rarity: "uncommon", icon: "⚔", points: 25,
  },
  "perry:chain_30": {
    id: "perry:chain_30", game: "perry",
    name: "Thirty",
    desc: "Answer 30 strikes in a row without a miss.",
    rarity: "rare", icon: "◆", points: 50,
  },
  "perry:chain_50": {
    id: "perry:chain_50", game: "perry",
    name: "Fifty",
    desc: "Answer 50 strikes in a row without a miss.",
    rarity: "epic", icon: "◈", points: 100,
  },
  "perry:no_miss_act1": {
    id: "perry:no_miss_act1", game: "perry",
    name: "Untouched by Fire",
    desc: "Reach Hollowmere without a single miss.",
    rarity: "rare", icon: "🔥", points: 50,
  },
  "perry:all_four": {
    id: "perry:all_four", game: "perry",
    name: "Four Roads, One Perry",
    desc: "Reach Hollowmere as all four starter bebs.",
    rarity: "epic", icon: "🜋", points: 100,
  },
  // ── LEVEL 2 — THE DROWNED MILE, added 2026-07-28 ──
  // NOTE FOR THE PROPAGATION PASS (Roadmap R-01): these five
  // deepen an existing debt, they do not create a new one. R-01
  // has always been "copy Perry's achievements.js verbatim into
  // every IE app" — it is the same single copy operation, with
  // five more rows in it. SCHEMA_VERSION does not move; additive
  // registry entries never bump it.
  "perry:reach_candlemarch": {
    id: "perry:reach_candlemarch", game: "perry",
    name: "The Far Bank",
    desc: "Cross the Drowned Mile and climb into Candlemarch.",
    rarity: "rare", icon: "🕯", points: 50,
  },
  // ══════════════════════════════════════════════════════════════════════
  //  TH'RAKK — the one the Tutorial does not ask you to win
  // ══════════════════════════════════════════════════════════════════════
  // caP'n, 2026-07-30: "the boss was tough, you weren't meant to beat it,
  //  so it's a super rare achievement really meant for new game plus."
  //
  // The Ridge Gate fight resolves perfectly well by simply OUTLASTING it —
  // the beats run out, the warden shambles off, the road goes on, and on a
  // teaching road that cannot kill you the outcome was never in doubt.
  // Breaking a Champion's wall through with a fresh beb's edge is the
  // other ending, and almost nobody will see it on a first walk. That is
  // the whole point of the entry: LEGENDARY, so it pays 4 ◆ on the shared
  // bounty ladder, and it is not hidden — a brag nobody knows exists is a
  // brag nobody goes back for.
  //
  // Awarded off the director's `ruin` event, so it fires wherever it
  // happens: on the tutorial, on a New Game Plus walk, or on a fifth
  // stroll down the Cinders Road with a crowned branch. Beating it is
  // beating it.
  "perry:thrakk": {
    id: "perry:thrakk", game: "perry",
    name: "Nobody Told You To Win",
    desc: "Break Th'rakk at the Ridge Gate. The road never asked you to.",
    rarity: "legendary", icon: "⛓", points: 150,
  },
  "perry:disciple": {
    id: "perry:disciple", game: "perry",
    name: "In His Master's Honour",
    desc: "Break the Disciple of T'yphiz and take the bill off it.",
    rarity: "epic", icon: "⚔", points: 75,
  },
  "perry:read_the_creature": {
    id: "perry:read_the_creature", game: "perry",
    name: "Read the Creature",
    desc: "Answer a foe that put the coals out, without a single miss.",
    rarity: "epic", icon: "👁", points: 80,
  },
  "perry:coin_purse": {
    id: "perry:coin_purse", game: "perry",
    name: "Deeply Unfair",
    desc: "Catch six Divine Crests out of the air in one Solrend.",
    rarity: "uncommon", icon: "🪙", points: 25,
  },
  "perry:the_answer": {
    id: "perry:the_answer", game: "perry",
    name: "The Answer",
    desc: "Speak the U'hlti — three Khao and two Anima, in one breath.",
    rarity: "rare", icon: "⚡", points: 50,
  },
  "perry:typhiz": {
    id: "perry:typhiz", game: "perry",
    name: "Not To Be Slept On",
    desc: "A beb did this.",
    rarity: "mythic", icon: "🦆", points: 250, hidden: true,
  },

  // ── THE SHEATHE'S ACHIEVEMENTS (caP'n, 2026-07-30) ────────────────────
  // Six, and five of them are things you do by NOT doing things. That is
  // deliberate and it is the shape caP'n asked for: once every Art lives
  // on a sword, the interesting brag is no longer "I got everything," it
  // is "look what I refused."
  //
  // A NOTE ON THE RESTRICTION FIVE. Each of them needs a run-long flag
  // that only ever goes one way — false the moment you break it, never
  // back. That is a cheap `stats.current` boolean per rule and it must be
  // checked at the SPEND, not at the finish, or a player who bought a
  // thing and never equipped it would still qualify. Wiring is R-54.
  "perry:branch_ascended": {
    id: "perry:branch_ascended", game: "perry",
    name: "Reedbrook's Last Green Thing",
    desc: "Light every rung on the Fire-Hardened Reed and Awaken it into its Lord form.",
    rarity: "legendary", icon: "🌿", points: 200,
  },
  "perry:branch_champion": {
    id: "perry:branch_champion", game: "perry",
    name: "With A Stick",
    desc: "Break the Champion at the end of the road holding the fully Ascended Reed.",
    rarity: "mythic", icon: "🌳", points: 250,
  },
  "perry:never_upgraded": {
    id: "perry:never_upgraded", game: "perry",
    name: "It Was Always Enough",
    desc: "Finish the game carrying the Fire-Hardened Reed, having never Ascended it once.",
    rarity: "mythic", icon: "🪵", points: 300, hidden: true,
  },
  "perry:no_treasures": {
    id: "perry:no_treasures", game: "perry",
    name: "Empty-Handed",
    desc: "Finish the game without ever buying a Treasure, Tome or Curio.",
    rarity: "legendary", icon: "🕳", points: 200, hidden: true,
  },
  "perry:no_arts": {
    id: "perry:no_arts", game: "perry",
    name: "Nothing To Say",
    desc: "Finish the game without ever speaking an Art.",
    rarity: "legendary", icon: "🤐", points: 200, hidden: true,
  },
  "perry:untouched": {
    id: "perry:untouched", game: "perry",
    name: "The Fen Never Laid A Paw On You",
    desc: "Finish the game without losing a single point of Anima.",
    rarity: "mythic", icon: "✨", points: 400, hidden: true,
  },

  // ── ANNIHILATION ─────────────────────────────────────────────────────
  // THE POINT OF THESE TWO IS IDENTITY, NOT CONTENT. Annihilation had
  // NO entries of its own and granted only `terror:` ids, so a player
  // who had never opened Terror could be recorded as a Terror player —
  // and `earnedGameSet()` reads that map to decide Two Worlds and
  // Citizen. A title that cannot name itself corrupts every meta
  // achievement that counts titles.
  //
  // Deliberately TWO, both hung off the one grant path Annihilation
  // actually has a live call site for (the Sunken Fold clear counter in
  // profileWrites). Low numbers first, on purpose — the thing being
  // tested here is that a badge fires at all, not that there are many.
  "annihilation:first_fold": {
    id: "annihilation:first_fold", game: "annihilation",
    name: "Into the Sunken Fold",
    desc: "Clear the Sunken Fold for the first time.",
    rarity: "uncommon", icon: "🌊", points: 25,
  },
  "annihilation:fold_ten": {
    id: "annihilation:fold_ten", game: "annihilation",
    name: "The Fold Knows You",
    desc: "Clear the Sunken Fold ten times.",
    rarity: "rare", icon: "❦", points: 50,
  },

  // ── EMPIRE (CROSS-GAME META) ──────────────────────────────────────────
  // These belong to no single game. Earning them requires touching more
  // than one IE title — they ARE the universe acknowledging itself.
  "empire:two_worlds": {
    id: "empire:two_worlds", game: "empire",
    name: "Two Worlds",
    desc: "Earn an achievement in two different IE games.",
    rarity: "rare", icon: "🌐", points: 60,
  },
  "empire:citizen": {
    id: "empire:citizen", game: "empire",
    name: "Citizen of the Empire",
    desc: "Earn an achievement in every active IE game.",
    rarity: "mythic", icon: "♛", points: 300,
  },
  // ── CROSS-GAME GATES (caP'n, 2026-08-13) ─────────────────────────────
  // "Maybe you need requirements from this game, bring the stuff over,
  // and boom, now the achievement unlocks — super rare, because they
  // require a whole extra game. That's a lot of effort."
  //
  // These are the FIRST THREE and there are deliberately only three.
  // Every one is mythic, which by THE THREE RANKS makes it Lord (◆5) —
  // an hour and a quarter of play, which is honest for something that
  // cannot be earned inside a single title at all.
  //
  // ⚠ EVERY REQUIREMENT BELOW IS AN ID THAT ALREADY EXISTS AND ALREADY
  //   HAS A LIVE GRANT PATH. A gate whose requirement is unearnable is
  //   not rare, it is broken, and the two are indistinguishable from
  //   the player's side — they just never get it and never know why.
  "empire:the_long_walk": {
    id: "empire:the_long_walk", game: "empire",
    name: "The Long Walk",
    desc: "Open a pack in Terror and land a parry in Perry. Two roads, one traveller.",
    rarity: "mythic", icon: "🜋", points: 250,
  },
  "empire:coin_and_crest": {
    id: "empire:coin_and_crest", game: "empire",
    name: "Coin and Crest",
    desc: "Roll at Snake Eyes, then bring it to the Well at W'ily's Pier.",
    rarity: "mythic", icon: "◈", points: 250,
  },
  "empire:fold_and_crown": {
    id: "empire:fold_and_crown", game: "empire",
    name: "The Fold and the Crown",
    desc: "Clear the Sunken Fold in Annihilation and put J'aeonxi down in Terror.",
    rarity: "mythic", icon: "♛", points: 300,
  },
  "empire:the_unwritten": {
    id: "empire:the_unwritten", game: "empire",
    name: "???",
    desc: "Some things are only discovered, never told.",
    rarity: "mythic", icon: "✶", points: 500,
    hidden: true,
  },
};

// ════════════════════════════════════════════════════════════════════
// REWARD GATES — THE LOCK TO THE ACHIEVEMENT KEY
// ────────────────────────────────────────────────────────────────────
// This is what makes the system SPECIAL: an achievement earned in ONE
// game can unlock a reward in ANOTHER. Each gate maps a reward (usually
// a radiant skin or cosmetic) to the achievement(s) required to access it.
//
// A game asking "can this player buy/equip reward X?" calls
// isRewardUnlocked(achievementMap, rewardId) with the player's earned
// achievement map (from listenToAchievements / getAchievements). No extra
// Firebase read needed — the gate is pure logic over data the game already
// holds. Cloudflare-verifiable because the achievement map is canonical.
//
//   type:  "radiant" | "cosmetic" | "title" | "pack"
//   requires: array of achievement IDs (ALL must be earned — AND logic)
//   anyOf:    array of achievement IDs (ANY one suffices — OR logic)
//   note:   designer-facing reason
//
// Use `requires` OR `anyOf`, not both, per gate.
// ════════════════════════════════════════════════════════════════════
export const REWARD_GATES = {
  // Beat J'aeonxi in Terror → unlock her radiant sheen offer everywhere.
  "radiant:jynxi_green": {
    type: "radiant",
    requires: ["terror:beat_jynxi"],
    note: "J'aeonxi's own green radiant — earned only by undoing the Nefarious.",
  },
  // Win big at the casino → a flashy radiant becomes purchasable in Terror.
  "radiant:high_roller_gold": {
    type: "radiant",
    requires: ["casino:high_roller"],
    note: "Cross-game: casino winnings buy you Terror shine.",
  },
  // Touch two worlds → a cross-game title.
  "title:two_worlds": {
    type: "title",
    requires: ["empire:two_worlds"],
    note: "Worn anywhere in the Empire.",
  },
  // The full-citizen flex: complete the universe.
  "radiant:imperial_aeterni": {
    type: "radiant",
    requires: ["empire:citizen"],
    note: "The crown radiant. Only true citizens of the Empire wear it.",
  },
  // Secret: the Unwritten. Never documented in-app — discovery only.
  "title:the_unwritten": {
    type: "title",
    requires: ["empire:the_unwritten"],
    note: "Community discovery only. Do not surface in tutorials or marketing.",
  },
};

// ════════════════════════════════════════════════════════════════════
// HELPERS — pure logic, no Firebase. Safe to call anywhere, any game.
// ════════════════════════════════════════════════════════════════════

// Does this id exist in the registry?
export const isValidAchievement = (id) => !!ACHIEVEMENTS[id];

// Look up a definition. Returns null for unknown ids (never throws).
export const getAchievement = (id) => ACHIEVEMENTS[id] || null;

// Rarity metadata for an achievement (falls back to "common").
export const getAchievementRarity = (id) => {
  const a = ACHIEVEMENTS[id];
  return ACHIEVEMENT_RARITY_META[a?.rarity] || ACHIEVEMENT_RARITY_META.common;
};

// The origin-game tint for an achievement (for UI accenting).
export const getAchievementTint = (id) => {
  const a = ACHIEVEMENTS[id];
  return GAME_NAMESPACES[a?.game]?.tint || GAME_NAMESPACES.empire.tint;
};

// Display fields, masked if it's a hidden achievement the player hasn't
// earned yet. Pass earned=true to reveal a hidden one once unlocked.
export const getAchievementDisplay = (id, earned = false) => {
  const a = ACHIEVEMENTS[id];
  if (!a) return { name: "Unknown", desc: "", icon: "?", masked: false };
  if (a.hidden && !earned) {
    return { name: "???", desc: "A secret yet to be discovered.", icon: "✶", masked: true };
  }
  return { name: a.name, desc: a.desc, icon: a.icon, masked: false };
};

// Every achievement id in the registry.
export const allAchievementIds = () => Object.keys(ACHIEVEMENTS);

// Every achievement belonging to a given game namespace.
export const achievementsForGame = (gameKey) =>
  Object.values(ACHIEVEMENTS).filter((a) => a.game === gameKey);

// The set of distinct games a player has earned at least one achievement in.
// achievementMap shape: { [id]: { earnedAt, ... } } (from Firebase).
export const earnedGameSet = (achievementMap) => {
  const games = new Set();
  for (const id of Object.keys(achievementMap || {})) {
    const a = ACHIEVEMENTS[id];
    if (a && a.game !== "empire") games.add(a.game); // empire isn't a "world"
  }
  return games;
};

// Total IE renown points a player has accrued.
export const totalRenown = (achievementMap) => {
  let sum = 0;
  for (const id of Object.keys(achievementMap || {})) {
    sum += ACHIEVEMENTS[id]?.points || 0;
  }
  return sum;
};

// ── REWARD-GATE EVALUATION (THE LOCK) ─────────────────────────────────
// Given the player's earned achievement map and a reward id, is the reward
// unlocked? Pure logic — the calling game already holds the map, so no
// Firebase round-trip. This is what every IE game calls to decide whether
// a cross-game reward is available.
export const isRewardUnlocked = (achievementMap, rewardId) => {
  const gate = REWARD_GATES[rewardId];
  if (!gate) return false;                 // unknown reward → locked by default
  const owned = (id) => !!(achievementMap && achievementMap[id]);

  if (Array.isArray(gate.requires)) {
    return gate.requires.every(owned);     // AND — all required
  }
  if (Array.isArray(gate.anyOf)) {
    return gate.anyOf.some(owned);         // OR — any one
  }
  return false;
};

// Convenience: list every reward this player has unlocked across all gates.
export const unlockedRewards = (achievementMap) =>
  Object.keys(REWARD_GATES).filter((rid) => isRewardUnlocked(achievementMap, rid));

// What's still missing for a locked reward (for "X of Y" UI hints).
// Returns the array of unmet achievement ids, or [] if already unlocked.
export const missingForReward = (achievementMap, rewardId) => {
  const gate = REWARD_GATES[rewardId];
  if (!gate) return [];
  const owned = (id) => !!(achievementMap && achievementMap[id]);
  const list = gate.requires || gate.anyOf || [];
  if (isRewardUnlocked(achievementMap, rewardId)) return [];
  return list.filter((id) => !owned(id));
};

// ── DERIVED-ACHIEVEMENT CHECK (THE META-WEAVE) ────────────────────────
// Some achievements are earned by EARNING OTHER achievements (the empire
// meta tier). After any grant, a game calls this to see which meta
// achievements should now also fire, then grants those too. Returns the
// array of meta achievement ids the player newly qualifies for but does
// NOT yet hold — caller grants each (grantAchievement is idempotent, so
// double-calls are harmless).
//
// activeGames: which game namespaces count toward "citizen" right now.
//
// THE CITIZEN GATE (IE_FIREBASE_LAW Part 2, rule 9). A title belongs on
// this list ONLY when it is publicly playable AND has live achievement
// grants firing. Registering a namespace in GAME_NAMESPACES is free and
// happens on day one; joining THIS list makes Citizen harder for everyone
// who hasn't earned it yet, so it happens on launch day and not before.
//
// If a title is listed here and cannot actually be played, Citizen becomes
// unearnable by anyone — which is exactly what "sol" was doing (Sol
// Survivor is unshipped; removed 2026-07-24). Put it back the day it goes
// live. Same for bebecitos and annihilation.
//
// THE RATCHET IS INTENTIONAL. Grants are append-only and irrevocable, so a
// player who earns Citizen against a 3-game roster keeps it forever once
// the roster is nine. That is citizenship in the Empire AS IT STOOD, not a
// completionist checkbox that decays. Nothing is ever revoked, nothing is
// time-limited, and latecomers simply live in a bigger Empire.
export const ACTIVE_GAMES = ["terror", "pier", "casino"];

export const deriveMetaAchievements = (achievementMap, activeGames = ACTIVE_GAMES) => {
  const map = achievementMap || {};
  const out = [];
  const has = (id) => !!map[id];
  const worlds = earnedGameSet(map);

  // Two Worlds — earned an achievement in 2+ distinct games.
  if (worlds.size >= 2 && !has("empire:two_worlds")) {
    out.push("empire:two_worlds");
  }
  // Citizen of the Empire — at least one achievement in every active game.
  const allCovered = activeGames.every((g) => worlds.has(g));
  if (allCovered && !has("empire:citizen")) {
    out.push("empire:citizen");
  }
  // Cross-game gates ride the SAME sweep. Folding them in here rather
  // than exporting a second function is the whole reason this needed no
  // call-site changes anywhere: grantAchievement() already runs this
  // after every grant, in every title, so a gate closes the moment its
  // last requirement lands no matter which game the player was in.
  out.push(...deriveCrossGame(map));
  return out;
};

// ════════════════════════════════════════════════════════════════════
// ⟡ CROSS-GAME GATES — the derived layer
// ────────────────────────────────────────────────────────────────────
// A gate is an AND over achievement ids from TWO DIFFERENT titles. It
// is DERIVED from the player's own achievement map, which every title
// already holds — so this costs zero extra Firebase reads, lands
// identically in every repo, and touches no existing entry. That is
// the same additive shape the three ranks used, and for the same
// reason: a registry that has drifted once will drift again under a
// hand-migration.
//
// ⚠ A GATE MUST NAME TWO DISTINCT GAMES. A "cross-game" achievement
//   satisfiable inside one title is just an achievement with extra
//   steps, and it will be earned by accident by someone who never left
//   home. assertGatesAreCrossGame() below turns that into a console
//   line instead of a slow surprise.
export const CROSS_GAME_GATES = {
  "empire:the_long_walk":  ["terror:first_pack", "perry:first_parry"],
  "empire:coin_and_crest": ["casino:first_roll", "pier:first_exchange"],
  "empire:fold_and_crown": ["annihilation:first_fold", "terror:beat_jynxi"],
};

export const deriveCrossGame = (achievementMap) => {
  const map = achievementMap || {};
  return Object.keys(CROSS_GAME_GATES).filter(
    (gid) => !map[gid] && CROSS_GAME_GATES[gid].every((req) => !!map[req])
  );
};

// What is still missing for a gate — for an honest "1 of 2" hint in a
// badge shelf. Returns [] when the gate is already satisfied.
export const missingForGate = (achievementMap, gateId) => {
  const reqs = CROSS_GAME_GATES[gateId];
  if (!reqs) return [];
  return reqs.filter((req) => !(achievementMap && achievementMap[req]));
};

// Self-check. Every requirement must EXIST, and a gate must span two
// distinct games. Call it once from a console; it returns [] when the
// gate table is sound.
export const auditCrossGameGates = () => {
  const problems = [];
  for (const [gid, reqs] of Object.entries(CROSS_GAME_GATES)) {
    if (!ACHIEVEMENTS[gid]) problems.push(`${gid}: gate has no ACHIEVEMENTS entry`);
    const games = new Set();
    for (const r of reqs) {
      if (!ACHIEVEMENTS[r]) { problems.push(`${gid}: requires unknown id ${r}`); continue; }
      games.add(ACHIEVEMENTS[r].game);
    }
    if (games.size < 2) problems.push(`${gid}: not cross-game — spans only ${[...games].join(", ")}`);
  }
  return problems;
};


// ════════════════════════════════════════════════════════════════════
// ⟡ THE THREE RANKS  (caP'n, 2026-08-12 · implemented 2026-08-13)
// ────────────────────────────────────────────────────────────────────
// Every achievement in the Empire is exactly one of:
//
//        Beb (◆1)   ·   Champ (◆3)   ·   Lord (◆5)
//
// THE RANK IS A TITLE. The payout is a CONSEQUENCE of the rank, never
// a number chosen per achievement — which is the whole point, because
// a per-achievement payout is how an economy drifts one generous
// ruling at a time until nobody can say what a Crest is worth.
// A fourth rank is a bug. (THE THREE-LEVEL LAW.)
//
// Priced against the unit settled in ieEconomy.js §1 — one ◆ is a
// quarter of a focused hour — the ladder reads honestly:
//        Beb   ◆1  ≈ 15 minutes
//        Champ ◆3  ≈ 45 minutes
//        Lord  ◆5  ≈ an hour and a quarter
//
// ── WHY THIS IS ADDITIVE AND NOT A REWRITE ──────────────────────────
// The roadmap called for migrating every entry by hand off the old
// six-step rarity ladder. That was the wrong shape, and doing it would
// have been worse than not doing it:
//
//   • The registry has ALREADY DRIFTED across six repos — Terror is at
//     SCHEMA_VERSION 1.2, Annihilation 1.1, three sit at 1.0, and
//     Bebecitos is a different file entirely. Hand-editing hundreds of
//     entries in six diverging files, the same day, is how you get a
//     seventh version.
//   • `rarity` is a STORED, SHIPPED field on live player rows. Deleting
//     it breaks every gallery and badge book reading it today.
//
// So the rank is DERIVED, not authored. Every achievement that exists
// has exactly one rank from the moment this block lands, in every repo,
// with zero entries touched and nothing to keep in sync. An entry may
// still pin `rank: "lord"` explicitly and that always wins — which is
// the escape hatch for the handful the mapping gets wrong, and the
// path by which authored ranks replace derived ones gradually instead
// of in one dangerous sweep. (THE EXCEPTION LAW.)
// ════════════════════════════════════════════════════════════════════

export const ACHIEVEMENT_RANKS = ["beb", "champ", "lord"];

export const ACHIEVEMENT_RANK_META = {
  beb:   { order: 0, label: "Beb",   short: "B", crests: 1, tint: "#7fb98a",
           blurb: "You did the thing. Everyone starts here." },
  champ: { order: 1, label: "Champ", short: "C", crests: 3, tint: "#4a88c8",
           blurb: "You did the thing well, or you did the hard thing." },
  lord:  { order: 2, label: "Lord",  short: "L", crests: 5, tint: "#d4a84b",
           blurb: "The top of the ladder. There is no fourth." },
};

if (ACHIEVEMENT_RANKS.length !== 3) {
  // eslint-disable-next-line no-console
  console.error("[IE/ACHIEVEMENTS] THE THREE-LEVEL LAW: three ranks. Found " + ACHIEVEMENT_RANKS.length + ".");
}

// The bridge off the legacy six-step ladder. Read it as: the bottom two
// are a Beb, the middle two are a Champ, the top two are a Lord.
const _RARITY_TO_RANK = {
  common: "beb", uncommon: "beb",
  rare: "champ", epic: "champ",
  legendary: "lord", mythic: "lord",
};

// An authored `rank` wins; otherwise derive. Unknown ids fall to beb —
// never to null, because a null rank means a null payout and a player
// silently earning nothing is worse than a player earning one Crest.
export const rankOf = (id) => {
  const a = ACHIEVEMENTS[id];
  if (a && ACHIEVEMENT_RANK_META[a.rank]) return a.rank;
  return _RARITY_TO_RANK[a?.rarity] || "beb";
};

export const rankMetaOf = (id) => ACHIEVEMENT_RANK_META[rankOf(id)];

// What this achievement pays, in ◆. THE ONLY PLACE A PAYOUT IS DECIDED.
// A call site that hardcodes a Crest amount for an achievement is a bug
// even when the number happens to be right today.
export const crestsFor = (id) => ACHIEVEMENT_RANK_META[rankOf(id)].crests;

// Every achievement at a rank — for a gallery that groups by title.
export const achievementsAtRank = (rank) =>
  Object.keys(ACHIEVEMENTS).filter((id) => rankOf(id) === rank);

// The audit the roadmap asked for, as a function instead of a chore.
// Returns every entry whose rank is DERIVED rather than authored, so
// "audit the registry against the three ranks" is one console line in
// any title rather than a read-through of six files.
export const unrankedAchievements = () =>
  Object.keys(ACHIEVEMENTS).filter((id) => !ACHIEVEMENT_RANK_META[ACHIEVEMENTS[id]?.rank]);

export default ACHIEVEMENTS;
